// AdminGate — auth gate per le pagine /admin/social.
//
// Perché esiste: le rotte admin vivono FUORI dal layout `Root`, quindi non
// passano da `useAuthentication`/`ProtectedRoute`. Questo componente è
// volutamente AUTONOMO: si aggancia direttamente a Firebase Auth, così
// funziona anche su un caricamento diretto dell'URL admin.
//
// Modello di sicurezza: riusa gli account StudierAI esistenti (niente signup
// separato, nessun dato perso). L'accesso è concesso solo se l'email loggata
// è nella allowlist `ADMIN_EMAILS`.
//
// Per aggiungere un admin: aggiungi l'email a REACT_APP_ADMIN_EMAILS
// (separate da virgola) oppure al fallback qui sotto.

import { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '@redux/firebase';
import './adminGate.scss';

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || 'studierai.info@gmail.com')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

const isAdmin = (user) =>
  !!user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

// Messaggi Firebase → italiano leggibile.
function authErrorMessage(code) {
  switch (code) {
    case 'auth/invalid-email':       return 'Email non valida.';
    case 'auth/user-disabled':       return 'Account disabilitato.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':  return 'Email o password errate.';
    case 'auth/too-many-requests':   return 'Troppi tentativi. Riprova tra poco.';
    case 'auth/network-request-failed': return 'Errore di rete. Controlla la connessione.';
    default:                         return 'Accesso non riuscito. Riprova.';
  }
}

export default function AdminGate() {
  // undefined = sto ancora controllando la sessione; null = nessun utente.
  const [authUser, setAuthUser] = useState(undefined);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (user) => setAuthUser(user || null)), []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // onAuthStateChanged aggiorna authUser e fa ripartire il render.
    } catch (err) {
      setError(authErrorMessage(err?.code));
      setSubmitting(false);
    }
  }, [email, password]);

  const handleSignOut = useCallback(() => {
    signOut(auth).catch(() => {});
    setEmail('');
    setPassword('');
  }, []);

  // ── Stato 1: controllo sessione in corso ──
  if (authUser === undefined) {
    return (
      <div className="admin-gate">
        <div className="admin-gate__spinner" aria-label="Verifica accesso" />
      </div>
    );
  }

  // ── Stato 2: autorizzato → mostra la pagina admin ──
  if (isAdmin(authUser)) {
    return <Outlet />;
  }

  // ── Stato 3: loggato ma non admin → accesso negato ──
  if (authUser) {
    return (
      <div className="admin-gate">
        <div className="admin-gate__card">
          <div className="admin-gate__eyebrow">Area riservata</div>
          <h1 className="admin-gate__title">Accesso negato</h1>
          <p className="admin-gate__lede">
            L'account <strong>{authUser.email}</strong> non è autorizzato a
            questa sezione.
          </p>
          <button
            type="button"
            className="admin-gate__btn admin-gate__btn--ghost"
            onClick={handleSignOut}
          >
            Esci e usa un altro account
          </button>
        </div>
      </div>
    );
  }

  // ── Stato 4: nessun utente → form di login ──
  return (
    <div className="admin-gate">
      <form className="admin-gate__card" onSubmit={handleSubmit}>
        <div className="admin-gate__eyebrow">Studio social</div>
        <h1 className="admin-gate__title">Area riservata</h1>
        <p className="admin-gate__lede">
          Accedi con il tuo account StudierAI autorizzato.
        </p>

        <label className="admin-gate__field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>

        <label className="admin-gate__field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        {error && (
          <p className="admin-gate__error" role="alert">{error}</p>
        )}

        <button
          type="submit"
          className="admin-gate__btn"
          disabled={submitting}
        >
          {submitting ? 'Accesso…' : 'Entra'}
        </button>
      </form>
    </div>
  );
}
